import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { exclusionApi, ExclusionRow } from '../api';

export interface DisplayExclusionRow extends ExclusionRow {
  product_title: string | null;
  plant_name: string | null;
  distribution_channel_desc: string | null;
  cg_1_business_segmentation_desc: string | null;
}

function uniqueLabel(labels: (string | null | undefined)[]): string | null {
  const names = Array.from(
    new Set(
      labels
        .map((label) => label?.trim())
        .filter((label): label is string => !!label)
    )
  );
  return names.length === 1 ? names[0] : null;
}

export function useExclusionLabels(
  rows: ExclusionRow[]
): DisplayExclusionRow[] {
  const { access_token, userId } = useAuthStore();
  const api = useMemo(() => exclusionApi(access_token), [access_token]);
  const client = useQueryClient();
  const scope = ['exclude-branch-lookup', userId];
  const options = { staleTime: 60000, retry: false };
  const channels = useQuery({
    ...options,
    queryKey: [...scope, 'channels'],
    queryFn: api.channels,
    enabled: rows.length > 0,
  });
  const plants = useQuery({
    ...options,
    queryKey: [...scope, 'plants', ''],
    queryFn: () => api.plants(''),
    enabled: rows.length > 0,
  });
  const cg1 = useQuery({
    ...options,
    queryKey: [...scope, 'cg1'],
    queryFn: api.cg1,
    enabled: rows.some((row) => row.cg_1_business_segmentation !== null),
  });
  const productCodes = Array.from(
    new Set(rows.map((row) => row.product_item_code))
  ).sort((left, right) => left - right);
  const products = useQuery({
    ...options,
    queryKey: [...scope, 'product-labels', productCodes],
    enabled: productCodes.length > 0,
    queryFn: async () => {
      const labels = new Map<number, string>();
      for (let offset = 0; offset < productCodes.length; offset += 5) {
        await Promise.all(
          productCodes.slice(offset, offset + 5).map(async (code) => {
            try {
              const result = await client.fetchQuery({
                ...options,
                queryKey: [...scope, 'products', String(code)],
                queryFn: () => api.products(String(code)),
              });
              const product = result.data.find(
                (product) => product.item_code === code
              );
              if (product) labels.set(code, product.title);
            } catch {
              // A failed description lookup must not hide the exclusion rule.
            }
          })
        );
      }
      return labels;
    },
  });
  return useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        product_title: products.data?.get(row.product_item_code) ?? null,
        plant_name: uniqueLabel(
          plants.data?.data
            .filter((plant) => plant.plant_id === String(row.plant))
            .map((plant) => plant.plant_name) ?? []
        ),
        distribution_channel_desc: uniqueLabel(
          channels.data?.data
            .filter(
              (channel) =>
                channel.distribution_channel === row.distribution_channel
            )
            .map((channel) => channel.distribution_channel_desc) ?? []
        ),
        cg_1_business_segmentation_desc: uniqueLabel(
          cg1.data?.data
            .filter(
              (scope) =>
                scope.cg_1_business_segmentation ===
                row.cg_1_business_segmentation
            )
            .map((scope) => scope.cg_1_business_segmentation_desc) ?? []
        ),
      })),
    [rows, products.data, plants.data, channels.data, cg1.data]
  );
}
